/* eslint-disable @wyz/module/no-relative-import */
import { IOptionItem, TFunction } from '@wyz/types';
import { DateUtils, invariant } from '@wyz/utils';
import { isNil } from 'lodash-es';
import {
  ISupplierTypeOptionItem,
  IVehicleTypeGroupList,
  TranslationKeys,
} from '~/common/types';
import { PRICING_POLICY_STATUS, SERVICE_TYPE } from '~/purchase/schemas';
import {
  ICreateRedistributionServicePricingPolicyPayload,
  IGetOneRedistributionServicePricingPolicy,
  IPolicyDefinitionValue,
  IPricingStatus,
  IRedistributionServicePricingDefinitionPayload,
  IRedistributionServicePricingForm,
  IRedistributionServiceType,
} from '~/purchase/types';
import {
  NO_REFINEMENT_VALUE,
  buildSupplierTypeLookup,
  buildVehicleTypeLookup,
  getLookupValue,
} from './common';
import { RUN_FLAT_VALUE } from './quote-service';

export const FORM_ID = 'redistribution-service-pricing-policy-form';

export const getTyreTypeColumns = (
  t: TFunction<TranslationKeys>,
  allVehicleTypeGroups: IVehicleTypeGroupList['data'],
  refineByTyreType: boolean,
  includeRunFlat = false,
): Array<IOptionItem> => {
  if (refineByTyreType) {
    const base =
      allVehicleTypeGroups
        .flatMap((item) => item.vehicleTypes)
        ?.map((vehicleType) => ({
          label: t(vehicleType.i18nKey),
          value: vehicleType.publicKey,
        })) ?? [];
    if (includeRunFlat) {
      base.push({ label: RUN_FLAT_VALUE, value: RUN_FLAT_VALUE });
    }
    return base;
  }
  return [{ label: '', value: NO_REFINEMENT_VALUE }];
};

export const formToPayload = (
  formData: IRedistributionServicePricingForm,
  status: IPricingStatus,
  customerId: string,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
  supplierTypes: Array<ISupplierTypeOptionItem>,
): ICreateRedistributionServicePricingPolicyPayload => {
  const { vehicleTypeLookup } = buildVehicleTypeLookup(vehicleTypeGroups);
  const supplierTypeLookup = buildSupplierTypeLookup(supplierTypes);
  return {
    pricingPolicyType: 'redistribution_service',
    name: formData.name,
    startDate: DateUtils.formatTimestamp(formData?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData?.endDate
      ? DateUtils.formatTimestamp(formData?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    criteria: [
      {
        criterion: '',
        id: '',
        code: 'CUSTOMER_ID',
        value: customerId,
      },
    ],
    definitions: [
      ...flattenOneTyreDeliveryService(
        formData,
        vehicleTypeLookup,
        supplierTypeLookup,
      ),
    ],
  };
};

export function payloadToForm(
  payload: IGetOneRedistributionServicePricingPolicy,
  supplierTypes: Array<ISupplierTypeOptionItem>,
): IRedistributionServicePricingForm {
  const prices: Record<
    IRedistributionServiceType,
    IGetOneRedistributionServicePricingPolicy['definitions']
  > = payload.definitions.reduce(
    (prices, definition) => ({
      ...prices,
      [definition.serviceType]: [...prices[definition.serviceType], definition],
    }),
    {
      [SERVICE_TYPE.ONE_TYRE_DELIVERY]: [],
    } as Record<
      IRedistributionServiceType,
      ICreateRedistributionServicePricingPolicyPayload['definitions']
    >,
  );

  return {
    name: payload.name,
    startDate: new Date(payload.formattedStartDate),
    endDate: payload.formattedEndDate
      ? new Date(payload.formattedEndDate)
      : undefined,
    defaultValue: {},
    oneTyreDelivery: {
      supplierTypes: prices.ONE_TYRE_DELIVERY.reduce<
        IRedistributionServicePricingForm['oneTyreDelivery']['supplierTypes']
      >((oneTyre, definition) => {
        const result = { ...oneTyre };
        const vehicleType = definition.tyreType?.key;
        invariant(vehicleType);
        const supplierTypeKey = definition.supplierType?.key;

        const supplierType = supplierTypes.find(
          (item) => item.id === Number(supplierTypeKey),
        )?.value;
        // append to result only if supplierType exists in the supplierTypes list
        if (supplierType) {
          if (isNil(result[supplierType])) {
            result[supplierType] = {
              tyreTypes: {},
            };
          }
          result[supplierType].tyreTypes[vehicleType] = {
            value: definition.priceValue.c,
          };
        }
        return result;
      }, {}),
    },
  };
}
// tyreOffer
const flattenOneTyreDeliveryService = (
  formData: IRedistributionServicePricingForm,
  vehicleTypeLookup: Record<string, IPolicyDefinitionValue>,
  supplierTypeLookup: Record<string, IPolicyDefinitionValue>,
): IRedistributionServicePricingDefinitionPayload[] => {
  const definitions: IRedistributionServicePricingDefinitionPayload[] = [];
  Object.entries(formData.oneTyreDelivery.supplierTypes || {}).forEach(
    ([supplierTypeCodename, supplierTypesRefinement]) => {
      Object.entries(supplierTypesRefinement.tyreTypes).forEach(
        ([tyreTypePublicKey, tyreTypesDef]) => {
          const tyreType =
            tyreTypePublicKey === NO_REFINEMENT_VALUE
              ? null
              : tyreTypePublicKey === RUN_FLAT_VALUE
                ? { key: RUN_FLAT_VALUE, name: RUN_FLAT_VALUE }
                : getLookupValue(tyreTypePublicKey, vehicleTypeLookup, false);
          const supplierType = getLookupValue(
            supplierTypeCodename,
            supplierTypeLookup,
            false,
          );
          // null is a valid value, undefined is not
          if (
            !isNil(tyreTypesDef.value) &&
            tyreType !== undefined &&
            supplierType !== undefined
          ) {
            definitions.push({
              priceValue: {
                c: tyreTypesDef.value,
                cg1: 0,
                cg2: 0,
                cg3: 0,
                cg4: 0,
                cg5: 0,
              },
              tyreType: tyreType,
              serviceType: 'ONE_TYRE_DELIVERY',
              serviceStatus: PRICING_POLICY_STATUS.ENABLED,
              supplierType,
            });
          }
        },
      );
    },
  );

  return definitions;
};

export const getInitialValues = (
  supplierTypes: Array<ISupplierTypeOptionItem>,
): IRedistributionServicePricingForm => {
  const SERVICE_PRICING_POLICY_DEFAULT_VALUES: IRedistributionServicePricingForm =
    {
      name: '',
      startDate: undefined as unknown as Date,
      defaultValue: supplierTypes.reduce(
        (acc, supplierTypeItem) => ({
          ...acc,
          [supplierTypeItem.value]: '',
        }),
        {},
      ),
      oneTyreDelivery: {
        supplierTypes: {},
      },
    };
  return {
    ...SERVICE_PRICING_POLICY_DEFAULT_VALUES,
  };
};
